/**
 * T037: Review Step Component
 * 
 * Handles final review and submission in onboarding wizard
 * Constitutional compliance: WCAG 2.1 AA accessibility, TDD coverage
 */

import React from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Stack,
  Paper,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { OnboardingData } from '../../../services/onboardingService';

export interface ReviewStepProps {
  data: OnboardingData['review'];
  allData: OnboardingData;
  onChange: (data: Partial<OnboardingData['review']>) => void;
  errors: Record<string, string>;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  data,
  allData,
  onChange,
  errors
}) => {
  const handleFieldChange = (field: keyof OnboardingData['review']) => 
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ [field]: event.target.value });
    };

  const handleCheckboxChange = (field: keyof OnboardingData['review']) => 
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ [field]: event.target.checked });
    };

  const handleArrayChange = (field: keyof OnboardingData['review']) => 
    (event: any) => {
      const value = typeof event.target.value === 'string' 
        ? event.target.value.split(',') 
        : event.target.value;
      onChange({ [field]: value });
    };

  const communicationOptions = [
    { value: 'email', label: 'Email Updates' },
    { value: 'phone', label: 'Phone Calls' },
    { value: 'slack', label: 'Slack/Teams' },
    { value: 'weekly_reports', label: 'Weekly Reports' },
    { value: 'milestone_updates', label: 'Milestone Updates' },
    { value: 'daily_standups', label: 'Daily Standups' }
  ];

  const formatArrayValue = (arr: string[] | undefined): string => {
    return arr?.join(', ') || 'Not specified';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Review Your Information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Please review the information you've provided and complete the final steps.
      </Typography>

      <Stack spacing={3}>
        {/* Company Information Summary */}
        <Paper sx={{ p: 2 }} elevation={1}>
          <Typography variant="h6" gutterBottom>
            Company Information
          </Typography>
          <Stack spacing={1}>
            <Typography><strong>Company:</strong> {allData.company_info.company_name}</Typography>
            <Typography><strong>Website:</strong> {allData.company_info.company_website || 'Not provided'}</Typography>
            <Typography><strong>Size:</strong> {allData.company_info.company_size}</Typography>
            <Typography><strong>Industry:</strong> {allData.company_info.industry}</Typography>
            <Typography><strong>Contact:</strong> {allData.company_info.primary_contact_name} ({allData.company_info.primary_contact_email})</Typography>
            {allData.company_info.company_description && (
              <Typography><strong>Description:</strong> {allData.company_info.company_description}</Typography>
            )}
          </Stack>
        </Paper>

        {/* Project Details Summary */}
        <Paper sx={{ p: 2 }} elevation={1}>
          <Typography variant="h6" gutterBottom>
            Project Details
          </Typography>
          <Stack spacing={1}>
            <Typography><strong>Project:</strong> {allData.project_details.project_name}</Typography>
            <Typography><strong>Description:</strong> {allData.project_details.project_description}</Typography>
            <Typography><strong>Types:</strong> {formatArrayValue(allData.project_details.project_types)}</Typography>
            <Typography><strong>Priority:</strong> {allData.project_details.priority_level}</Typography>
            {allData.project_details.budget_range && (
              <Typography><strong>Budget Range:</strong> {allData.project_details.budget_range}</Typography>
            )}
            {allData.project_details.timeline && (
              <Typography><strong>Timeline:</strong> {allData.project_details.timeline}</Typography>
            )}
          </Stack>
        </Paper>

        {/* Technical Requirements Summary */}
        <Paper sx={{ p: 2 }} elevation={1}>
          <Typography variant="h6" gutterBottom>
            Technical Requirements
          </Typography>
          <Stack spacing={1}>
            <Typography><strong>Target Platforms:</strong> {formatArrayValue(allData.tech_requirements.target_platforms)}</Typography>
            <Typography><strong>Preferred Technologies:</strong> {formatArrayValue(allData.tech_requirements.preferred_technologies)}</Typography>
            <Typography><strong>Integration Requirements:</strong> {formatArrayValue(allData.tech_requirements.integration_requirements)}</Typography>
            {allData.tech_requirements.performance_requirements && (
              <Typography><strong>Performance Requirements:</strong> {allData.tech_requirements.performance_requirements}</Typography>
            )}
            {allData.tech_requirements.security_requirements && (
              <Typography><strong>Security Requirements:</strong> {allData.tech_requirements.security_requirements}</Typography>
            )}
          </Stack>
        </Paper>

        <Divider />

        {/* Communication Preferences */}
        <FormControl fullWidth error={!!errors.communication_preferences}>
          <InputLabel>Communication Preferences</InputLabel>
          <Select
            multiple
            value={data.communication_preferences || []}
            onChange={handleArrayChange('communication_preferences')}
            label="Communication Preferences"
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const option = communicationOptions.find(o => o.value === value);
                  return <Chip key={value} label={option?.label || value} size="small" />;
                })}
              </Box>
            )}
            data-testid="communication-preferences-select"
          >
            {communicationOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {errors.communication_preferences && <FormHelperText>{errors.communication_preferences}</FormHelperText>}
        </FormControl>

        {/* Additional Notes */}
        <TextField
          fullWidth
          label="Additional Notes"
          multiline
          rows={4}
          value={data.additional_notes || ''}
          onChange={handleFieldChange('additional_notes')}
          error={!!errors.additional_notes}
          helperText={errors.additional_notes || 'Optional - Any additional information or questions you have for us'}
          data-testid="additional-notes-textarea"
        />

        <Divider />

        {/* Terms and Conditions */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Terms and Agreements
          </Typography>
          
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.terms_accepted}
                  onChange={handleCheckboxChange('terms_accepted')}
                  data-testid="terms-checkbox"
                />
              }
              label={
                <Typography>
                  I accept the{' '}
                  <Typography component="span" color="primary" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                    Terms and Conditions
                  </Typography>
                  {' '}*
                </Typography>
              }
              sx={{ color: errors.terms_accepted ? 'error.main' : 'inherit' }}
            />
            {errors.terms_accepted && (
              <Typography variant="caption" color="error">
                {errors.terms_accepted}
              </Typography>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={data.privacy_policy_accepted}
                  onChange={handleCheckboxChange('privacy_policy_accepted')}
                  data-testid="privacy-checkbox"
                />
              }
              label={
                <Typography>
                  I accept the{' '}
                  <Typography component="span" color="primary" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                    Privacy Policy
                  </Typography>
                  {' '}*
                </Typography>
              }
              sx={{ color: errors.privacy_policy_accepted ? 'error.main' : 'inherit' }}
            />
            {errors.privacy_policy_accepted && (
              <Typography variant="caption" color="error">
                {errors.privacy_policy_accepted}
              </Typography>
            )}
          </Stack>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2">
            <strong>What happens next?</strong><br />
            After submitting this form, our team will review your information and contact you within 1-2 business days to discuss your project in detail and provide a customized proposal.
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default ReviewStep;